'use client'

import { useCallback, useEffect, useId, useRef, useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import MonoLabel from './MonoLabel'
import { mobileNav, navIndex } from '@/lib/navigation'
import { revision, siteConfig } from '@/lib/site-config'

const FOCUSABLE = 'a[href], button:not([disabled])'

/**
 * Full-screen mobile index. The trigger lives in the header's right cell and
 * is replaced in place by an inverted Close button while the overlay is open.
 */
export default function MobileMenu() {
  const [open, setOpen] = useState(false)
  const pathname = usePathname()
  const overlayId = useId()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)
  const openedAt = useRef<string | null>(null)
  const wasOpen = useRef(false)

  /**
   * The trigger doubles as the Close button and sits outside the overlay, so
   * the trap spans both to keep it reachable by keyboard.
   */
  const focusables = useCallback(() => {
    const inOverlay = Array.from(overlayRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])
    const trigger = triggerRef.current
    if (!trigger) return inOverlay
    // Sort into document order so the cycle matches real tab order.
    return [trigger, ...inOverlay].sort((a, b) =>
      a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
    )
  }, [])

  const close = useCallback(() => setOpen(false), [])

  // Close on route change.
  useEffect(() => {
    if (!open) {
      openedAt.current = null
      return
    }
    if (openedAt.current === null) {
      openedAt.current = pathname
    } else if (openedAt.current !== pathname) {
      setOpen(false)
    }
  }, [pathname, open])

  // Lock body scroll while open.
  useEffect(() => {
    if (!open) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [open])

  // Move focus into the overlay on open, and back to the trigger on close.
  useEffect(() => {
    if (open) {
      wasOpen.current = true
      overlayRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus()
    } else if (wasOpen.current) {
      wasOpen.current = false
      triggerRef.current?.focus()
    }
  }, [open])

  // Escape to close; Tab cycles within the overlay.
  useEffect(() => {
    if (!open) return

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault()
        setOpen(false)
        return
      }
      if (event.key !== 'Tab') return

      const nodes = focusables()
      if (nodes.length === 0) return

      const first = nodes[0]
      const last = nodes[nodes.length - 1]
      const active = document.activeElement

      if (event.shiftKey && active === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, focusables])

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-controls={overlayId}
        className={`flex min-h-[48px] min-w-[88px] items-center justify-center gap-2 border-l border-ink px-4 transition-colors duration-150 ${
          open ? 'bg-ink text-paper' : 'text-ink'
        }`}
      >
        {open ? <CloseIcon /> : <MenuIcon />}
        <MonoLabel>{open ? 'Close' : 'Menu'}</MonoLabel>
      </button>

      {open && (
        <div
          ref={overlayRef}
          id={overlayId}
          role="dialog"
          aria-modal="true"
          aria-label="Index"
          className="fixed inset-0 z-40 flex flex-col bg-paper pt-[48px]"
        >
          <div className="border-b border-ink px-5 py-3">
            <MonoLabel className="text-accent">Index</MonoLabel>
          </div>

          <nav aria-label="Mobile navigation" className="flex-1 overflow-y-auto">
            {mobileNav.map((link, position) => {
              const current = pathname === link.href
              return (
                <Link
                  key={link.href}
                  href={link.href}
                  onClick={close}
                  aria-current={current ? 'page' : undefined}
                  className={`grid grid-cols-[40px_1fr] items-baseline px-5 py-[14px] ${
                    position === mobileNav.length - 1
                      ? 'border-b border-ink'
                      : 'border-b border-rule'
                  }`}
                >
                  <MonoLabel className={current ? 'text-accent' : 'text-ink-3'}>
                    {navIndex(position)}
                  </MonoLabel>
                  <span className="text-[28px] font-medium tracking-[-0.02em]">{link.name}</span>
                </Link>
              )
            })}
          </nav>

          <div className="border-t border-ink px-5 py-4">
            <MonoLabel small className="block text-ink-3">
              Availability
            </MonoLabel>
            <p className="mt-1 text-[15px] text-accent">{siteConfig.availability}</p>
          </div>

          <div className="grid grid-cols-2 border-t border-ink">
            <div className="border-r border-ink px-5 py-3">
              <MonoLabel small className="text-ink-3">
                {revision()}
              </MonoLabel>
            </div>
            <div className="px-5 py-3 text-right">
              <MonoLabel small className="text-ink-3">
                {siteConfig.location}
              </MonoLabel>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

function MenuIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" fill="none">
      <path d="M1 5h14M1 11h14" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  )
}

function CloseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" fill="none">
      <path d="M2 2l12 12M14 2L2 14" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  )
}
